import { InvoiceAdjustmentTypeEnum } from 'parkchirp-shared/enums';
import React, { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useMemo, useState } from 'react';

type AdjustInvoiceProviderProps = {
	children: ReactNode;
};

export interface AdjustInvoiceContextInterface {
	initialized: boolean;
	setInitialized: Dispatch<SetStateAction<boolean>>;
	isAdjustingEachLineItem: boolean;
	setIsAdjustingEachLineItem: Dispatch<SetStateAction<boolean>>;
}

type LineItemEntryInput = {
	id?: number;
	adjustmentType?: InvoiceAdjustmentTypeEnum;
	lineItemId?: string;
	amount: number;
	amountTax1: number;
	amountTax2: number;
	amountTax3: number;
	amountTax4: number;
};

type LineItemEntryMapping = Map<string, Array<LineItemEntryInput>>;

const defaultAppContext = {
	isAdjustingEachLineItem: null,
	setIsAdjustingEachLineItem: (value: boolean) => {},
	lineItemAdjustmentMap: new Map<string, Array<LineItemEntryInput>>(),
	setLineItemAdjustmentMap: (value: LineItemEntryMapping) => {},
	// the below placeholders are here so typescript understands that the
	// function is supposed to return type LineItemEntryInput
	//
	getLineItemAdjustmentTotalByLineItemId: (value: string) => {
		const placeholder: LineItemEntryInput = null;
		return placeholder;
	},
	getLineItemAdjustmentTotal: () => {
		const placeholder: LineItemEntryInput = null;
		return placeholder;
	},
	getAllLineItemAdjustmentsFromMap: () => {
		const placeholder: LineItemEntryInput[] = [];
		return placeholder;
	},
};

export const AdjustInvoiceContext = createContext(defaultAppContext);

const AdjustInvoiceContextProvider = ({ children }: AdjustInvoiceProviderProps) => {
	const [isAdjustingEachLineItem, setIsAdjustingEachLineItem] = useState<boolean>(false);
	// maps line item id to an array of adjustments
	const [lineItemAdjustmentMap, setLineItemAdjustmentMap] = useState<LineItemEntryMapping>(new Map());

	const getLineItemAdjustmentTotalByLineItemId = useCallback(
		(id: string): LineItemEntryInput => {
			const maybeList = lineItemAdjustmentMap.get(id);
			const result: LineItemEntryInput = {
				amount: 0,
				amountTax1: 0,
				amountTax2: 0,
				amountTax3: 0,
				amountTax4: 0,
			};
			if (maybeList) {
				for (const entry of maybeList) {
					if (
						[
							InvoiceAdjustmentTypeEnum.ADJUSTMENT_POSITIVE,
							InvoiceAdjustmentTypeEnum.TERMINATION_ADJUSTMENT_POSITIVE,
						].includes(InvoiceAdjustmentTypeEnum[entry.adjustmentType])
					) {
						result.amount += entry.amount;
						result.amountTax1 += entry.amountTax1;
						result.amountTax2 += entry.amountTax2;
						result.amountTax3 += entry.amountTax3;
						result.amountTax4 += entry.amountTax4;
					} else {
						result.amount -= entry.amount;
						result.amountTax1 -= entry.amountTax1;
						result.amountTax2 -= entry.amountTax2;
						result.amountTax3 -= entry.amountTax3;
						result.amountTax4 -= entry.amountTax4;
					}
				}
			}
			return result;
		},
		[lineItemAdjustmentMap],
	);

	const getLineItemAdjustmentTotal = useCallback((): LineItemEntryInput => {
		const result: LineItemEntryInput = {
			amount: 0,
			amountTax1: 0,
			amountTax2: 0,
			amountTax3: 0,
			amountTax4: 0,
		};

		lineItemAdjustmentMap.forEach((_, key) => {
			const lineItemTotal = getLineItemAdjustmentTotalByLineItemId(key);
			result.amount += lineItemTotal.amount;
			result.amountTax1 += lineItemTotal.amountTax1;
			result.amountTax2 += lineItemTotal.amountTax2;
			result.amountTax3 += lineItemTotal.amountTax3;
			result.amountTax4 += lineItemTotal.amountTax4;
		});
		return result;
	}, [lineItemAdjustmentMap]);

	const getAllLineItemAdjustmentsFromMap = useCallback((): LineItemEntryInput[] => {
		let result = [];
		lineItemAdjustmentMap.forEach((_, key) => {
			const maybeList = lineItemAdjustmentMap.get(key);
			if (maybeList.length) {
				result = [...result, ...maybeList];
			}
		});
		return result;
	}, [lineItemAdjustmentMap]);

	const values = useMemo(
		() => ({
			isAdjustingEachLineItem,
			setIsAdjustingEachLineItem,
			lineItemAdjustmentMap,
			setLineItemAdjustmentMap,
			getLineItemAdjustmentTotalByLineItemId,
			getLineItemAdjustmentTotal,
			getAllLineItemAdjustmentsFromMap,
		}),
		[
			isAdjustingEachLineItem,
			lineItemAdjustmentMap,
			getLineItemAdjustmentTotalByLineItemId,
			getLineItemAdjustmentTotal,
			getAllLineItemAdjustmentsFromMap,
		],
	);

	return <AdjustInvoiceContext.Provider value={values}>{children}</AdjustInvoiceContext.Provider>;
};

export default AdjustInvoiceContextProvider;
